import type { Attachment } from '@live-stream/shared'

/**
 * The pure part of chat attachments: what a file is called on its card, and the upload itself as a promise with progress.
 * The store decides when; this file only knows how.
 */

/** A short badge for the card: the extension people know, or a family name. */
export function fileLabel(att: Pick<Attachment, 'name' | 'contentType'>): string {
  const dot = att.name.lastIndexOf('.')
  const ext = dot > 0 && att.name.length - dot <= 6 ? att.name.slice(dot + 1).toUpperCase() : ''
  if (ext) return ext
  const [family] = att.contentType.split('/')
  switch (family) {
    case 'image':
      return 'IMAGE'
    case 'video':
      return 'VIDEO'
    case 'audio':
      return 'AUDIO'
    case 'text':
      return 'TEXT'
    default:
      return 'FILE'
  }
}

/** True when the browser will show it inline as a picture. Decided by the server's verdict, not the name. */
export const isImage = (att: Pick<Attachment, 'kind'>): boolean => att.kind === 'image'

export interface PutOptions {
  url: string
  headers: Record<string, string>
  body: Blob
  onProgress?: (fraction: number) => void
  signal?: AbortSignal
}

/**
 * PUT the bytes to the signed address with progress. XMLHttpRequest, not
 * fetch, because fetch cannot report upload progress and a 20 MB file
 * with no bar is a file that looks stuck. A relative address is our own
 * API standing in for the bucket in development; it gets the header that
 * keeps a tunnel from answering with a web page. An absolute one is the
 * bucket, which gets exactly the headers it was signed for and no more —
 * an extra header there fails the CORS preflight.
 */
export function putWithProgress(opts: PutOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', opts.url)
    for (const [name, value] of Object.entries(opts.headers)) xhr.setRequestHeader(name, value)
    if (opts.url.startsWith('/')) xhr.setRequestHeader('ngrok-skip-browser-warning', '1')
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && opts.onProgress) opts.onProgress(event.loaded / event.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`The upload was refused (HTTP ${xhr.status}).`))
    }
    xhr.onerror = () =>
      reject(
        new Error(
          opts.url.startsWith('/')
            ? 'The upload did not reach the server.'
            : 'The upload did not reach storage — check the bucket allows this origin (CORS).',
        ),
      )
    xhr.onabort = () => reject(new DOMException('Upload cancelled.', 'AbortError'))
    opts.signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(opts.body)
  })
}

/** The files a drop or a paste brought, or none. */
export function filesFrom(transfer: DataTransfer | null | undefined): File[] {
  if (!transfer) return []
  const out: File[] = []
  for (const item of Array.from(transfer.items ?? [])) {
    if (item.kind !== 'file') continue
    const file = item.getAsFile()
    if (file) out.push(file)
  }
  if (out.length > 0) return out
  return Array.from(transfer.files ?? [])
}
